import {
  Box,
  ButtonGroup,
  IconButton,
  Input,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import TableData from 'components/TableData';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaHeartBroken } from 'react-icons/fa';
import { Column } from 'react-table';
import { getAccounts, toggleAccountFavorite } from 'services/api.service';
import { useDebounce } from 'react-use';

interface FavoriteAccount {
  id: string;
  accountHolder: string | null;
  pix: string | null;
  agency: string | null;
  account: string | null;
  bankIspb: string | null;
}

export default function FavoritesPage() {
  const toast = useToast();
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [data, setData] = useState<FavoriteAccount[]>([]);
  useDebounce(() => setDebouncedFilter(filter), 300, [filter]);

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await getAccounts({
        filter: debouncedFilter || undefined,
        isFavorite: true,
        page: 0,
        limit: 100,
      });
      setData(res.registers || []);
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar favorecidos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [debouncedFilter, toast]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleRemoveFavorite = async (id: string) => {
    try {
      await toggleAccountFavorite(id, false);
      toast({
        title: 'Sucesso',
        description: 'Favorecido removido',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchFavorites();
    } catch {
      toast({
        title: 'Erro',
        description: 'Erro ao remover favorecido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          Header: 'Nome',
          accessor: 'accountHolder',
          Cell: ({ value }: any) => value || '---',
        },
        {
          Header: 'Chave PIX / Dados bancários',
          accessor: 'pix',
          Cell: ({ row }: any) => {
            const { pix, agency, account } = row.original;
            return pix || `${agency || ''} - ${account || ''}`;
          },
        },
        {
          Header: 'Banco',
          accessor: 'bankIspb',
          Cell: ({ value }: any) => value || '---',
        },
      ] as Column[],
    [],
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Input
        placeholder="Buscar por nome ou chave PIX..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        mb={4}
        size="sm"
        variant="outline"
        rounded="sm"
      />
      <TableData
        title="Favorecidos"
        pageEnable={true}
        actions={(row: any) => (
          <ButtonGroup>
            <Tooltip label="Remover favorecido">
              <IconButton
                onClick={() => handleRemoveFavorite(row.id)}
                variant="outline"
                size="sm"
                colorScheme="red"
                aria-label="Remover favorecido"
                icon={<FaHeartBroken />}
              />
            </Tooltip>
          </ButtonGroup>
        )}
        columnsData={columns}
        tableData={data}
      />
    </Box>
  );
}
